import {i18n} from '@online-offer/i18n';
import {Box, Heading, Hide, HStack, Image, Show, Space, Stack, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {isURLDangerous} from 'shared/web';

import {Button} from '../../components/Button/Button';
import {Separator} from '../../components/Separator/Separator';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {CarPassValidity} from './CarPassValidity';

const CAR_PASS_LOGO_SRC = '../../assets/images/car-pass-logo.png';
const CAR_PASS_LOGO_ASPECT_RATIO = 3508 / 1299;

export function CarPass() {
  const {carPassDocumentPublicUrl, carPassDocumentDownloadedAt} = useGetOnlineOfferData();

  // Only shown when a Car-Pass document exists; bail on unsafe URLs (defense-in-depth,
  // mirrors the isURLDangerous guard the Link component applies in the print layout).
  if (isNilOrEmpty(carPassDocumentPublicUrl) || isURLDangerous(carPassDocumentPublicUrl)) {
    return null;
  }

  const handleView = () => {
    window.open(carPassDocumentPublicUrl!, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Box
        overflow="hidden"
        position="relative"
        borderRadius="medium"
        paddingVertical={8}
        paddingHorizontal={[6, 8]}
        data-testid="carPass"
      >
        <CarPassGradientBackground />
        <Box position="relative">
          {/* Mobile: 4-row stack per Figma (logo+heading, buttons, separator, date). */}
          <Show onMobile>
            <VStack spacing={6}>
              <HStack spacing={6} align="center" justify="space-between">
                <Box flexShrink={0}>
                  <Image
                    isLazy
                    src={CAR_PASS_LOGO_SRC}
                    alt="Car-Pass"
                    ratio={CAR_PASS_LOGO_ASPECT_RATIO}
                    width="100%"
                    maxWidth="4.0625rem"
                    maxHeight={6}
                    fit="contain"
                  />
                </Box>
                <Heading size={3}>{i18n.t('carPass.heading')}</Heading>
              </HStack>
              <HStack spacing={4} align="center">
                {/* "Stáhnout PDF" hidden for now — no real download URL yet. */}
                <Button
                  title={i18n.t('carPass.view')}
                  variant="inverted"
                  rightIcon="action/open_in_new"
                  isFullWidth
                  onClick={handleView}
                  data-testid="carPassView"
                />
              </HStack>
              <Separator />
              <CarPassValidity downloadedAt={carPassDocumentDownloadedAt} />
            </VStack>
          </Show>
          {/* Tablet+ desktop: single row (logo | heading+date | buttons). */}
          <Hide onMobile>
            <Stack direction="row" spacing={12} align="center">
              <Box flexShrink={0}>
                <Image
                  isLazy
                  src={CAR_PASS_LOGO_SRC}
                  alt="Car-Pass"
                  ratio={CAR_PASS_LOGO_ASPECT_RATIO}
                  width="100%"
                  maxWidth="5.375rem"
                  maxHeight={8}
                  fit="contain"
                />
              </Box>
              <Box flexGrow={1}>
                <VStack spacing={2} align="flex-start">
                  <Heading size={3}>{i18n.t('carPass.heading')}</Heading>
                  <CarPassValidity downloadedAt={carPassDocumentDownloadedAt} />
                </VStack>
              </Box>
              {/* "Stáhnout PDF" hidden for now — no real download URL yet. */}
              <Button
                title={i18n.t('carPass.view')}
                variant="inverted"
                rightIcon="action/open_in_new"
                onClick={handleView}
                data-testid="carPassView"
              />
            </Stack>
          </Hide>
        </Box>
      </Box>
      <Space vertical={8} />
      <Separator />
      <Space vertical={8} />
    </>
  );
}

const CarPassGradientBackground = styled.div`
  position: absolute;
  pointer-events: none;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgb(247, 250, 245) 0%,
    rgb(235, 247, 236) 50%,
    rgb(248, 249, 235) 100%
  );
`;
